import { create } from 'zustand';
import { persist } from 'zustand/middleware';
export const useThemeStore = create()(persist((set) => ({
    theme: 'light',
    accentColor: 'blue', // Default to metallic blue
    toggleTheme: () => set((state) => ({
        theme: state.theme === 'dark' ? 'light' : 'dark',
    })),
    setTheme: (theme) => set({ theme }),
    toggleAccentColor: () => set((state) => ({
        accentColor: state.accentColor === 'gold' ? 'blue' : 'gold',
    })),
    setAccentColor: (color) => set({ accentColor: color }),
}), {
    name: 'theme-storage',
}));
// Accent color definitions
const accentColors = {
    gold: {
        main: '#B8860B',
        light: '#D4A634',
        dark: '#8B6914',
        glow: 'rgba(184, 134, 11, 0.15)',
        bg: 'rgba(184, 134, 11, 0.08)',
    },
    blue: {
        main: '#4A90E2', // Metallic blue
        light: '#64A8F0', // Lighter blue
        dark: '#357ABD', // Darker blue
        glow: 'rgba(74, 144, 226, 0.15)',
        bg: 'rgba(74, 144, 226, 0.08)',
    },
};
// CSS variables for themes
export const themes = {
    dark: {
        '--bg-primary': '#0C0A09',
        '--bg-secondary': '#1C1917',
        '--bg-tertiary': '#292524',
        '--text-primary': '#FAFAF9',
        '--text-secondary': 'rgba(250, 250, 249, 0.75)',
        '--text-tertiary': 'rgba(250, 250, 249, 0.5)',
        '--text-muted': '#A8A29E',
        '--border-color': 'rgba(250, 250, 249, 0.1)',
        '--border-color-hover': 'rgba(250, 250, 249, 0.2)',
        '--accent-color': '#FAFAF9',
        '--success-color': '#4ADE80',
        '--success-bg': 'rgba(74, 222, 128, 0.12)',
        '--error-color': '#F87171',
        '--error-bg': 'rgba(248, 113, 113, 0.12)',
        '--warning-color': '#FACC15',
        '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.3)',
        '--shadow-md': '0 4px 12px rgba(0, 0, 0, 0.4)',
        '--shadow-lg': '0 8px 24px rgba(0, 0, 0, 0.5)',
    },
    light: {
        '--bg-primary': '#FAFAF9',
        '--bg-secondary': '#FFFFFF',
        '--bg-tertiary': '#F5F5F4',
        '--text-primary': '#1C1917',
        '--text-secondary': '#44403C',
        '--text-tertiary': '#78716C',
        '--text-muted': '#A8A29E',
        '--border-color': '#E7E5E4',
        '--border-color-hover': '#D6D3D1',
        '--accent-color': '#1C1917',
        '--success-color': '#16A34A',
        '--success-bg': 'rgba(22, 163, 74, 0.1)',
        '--error-color': '#DC2626',
        '--error-bg': 'rgba(220, 38, 38, 0.1)',
        '--warning-color': '#CA8A04',
        '--shadow-sm': '0 1px 2px rgba(28, 25, 23, 0.05)',
        '--shadow-md': '0 4px 12px rgba(28, 25, 23, 0.08)',
        '--shadow-lg': '0 8px 24px rgba(28, 25, 23, 0.12)',
    },
};
// Get accent color variables based on selected color
const getAccentVars = (accentColor) => {
    const colors = accentColors[accentColor];
    return {
        '--accent-gold': colors.main,
        '--accent-gold-light': colors.light,
        '--accent-gold-dark': colors.dark,
        '--accent-gold-glow': colors.glow,
        '--accent-gold-bg': colors.bg,
        '--shadow-glow': `0 0 30px ${colors.glow}`,
    };
};
// Apply theme to document
export const applyTheme = (theme) => {
    const root = document.documentElement;
    const themeVars = themes[theme];
    Object.entries(themeVars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });
    // Add theme class to body
    document.body.className = `theme-${theme}`;
};
// Apply accent color to document
export const applyAccentColor = (accentColor) => {
    const root = document.documentElement;
    const accentVars = getAccentVars(accentColor);
    Object.entries(accentVars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });
};
